/**
 * SystemBuilderTooltips - Tooltip management component
 *
 * Handles all tooltip visibility and messaging:
 * - Tab prompt tooltips (shown on both desktop and mobile tabs)
 * - Mobile contextual messages (layer complete, product added)
 * - Drawer starter message (shown when no products selected)
 *
 * Listens for:
 * - STATE_CHANGED: Updates tooltips when products are selected/removed
 * - LAYER_CHANGED: Dismisses tooltips when user navigates to a layer
 * - SUBSYSTEM_CHANGED: Updates tooltips when user switches subsystems
 * - PRODUCT_SELECTED: Shows product added message on mobile
 *
 * @class
 * @extends {window.BAO.CustomElement}
 */
class SystemBuilderTooltips extends window.BAO.CustomElement() {
  lastProductAdded = null
  drawerMessageDismissed = false

  connectedCallback () {
    window.BAO.SystemBuilder.attach(this)
    super.connectedCallback()
  }

  setupListeners () {
    super.setupListeners()

    if (
      window.BAO.eventBus &&
      window.BAO.EVENTS?.SYSTEM_BUILDER?.STATE_CHANGED
    ) {
      window.BAO.eventBus.addEventListener(
        window.BAO.EVENTS.SYSTEM_BUILDER.STATE_CHANGED,
        this.#onStateChanged.bind(this),
      )
    }

    if (
      window.BAO.eventBus &&
      window.BAO.EVENTS?.SYSTEM_BUILDER?.LAYER_CHANGED
    ) {
      window.BAO.eventBus.addEventListener(
        window.BAO.EVENTS.SYSTEM_BUILDER.LAYER_CHANGED,
        this.#onLayerChanged.bind(this),
      )
    }

    if (
      window.BAO.eventBus &&
      window.BAO.EVENTS?.SYSTEM_BUILDER?.SUBSYSTEM_CHANGED
    ) {
      window.BAO.eventBus.addEventListener(
        window.BAO.EVENTS.SYSTEM_BUILDER.SUBSYSTEM_CHANGED,
        this.#onSubsystemChanged.bind(this),
      )
    }

    if (
      window.BAO.eventBus &&
      window.BAO.EVENTS?.SYSTEM_BUILDER?.PRODUCT_SELECTED
    ) {
      window.BAO.eventBus.addEventListener(
        window.BAO.EVENTS.SYSTEM_BUILDER.PRODUCT_SELECTED,
        this.#onProductSelected.bind(this),
      )
    }

    this.sharedEls.tab?.elements.forEach((tabEl) => {
      this.listeners.add(tabEl, 'click', this.handleTabClick.bind(this))
    })

    const drawerCloseButtons =
      this.sharedEls['drawer-message-close']?.elements || []

    drawerCloseButtons.forEach((closeEl) => {
      this.listeners.add(
        closeEl,
        'click',
        this.handleDrawerMessageDismiss.bind(this),
      )
    })

    requestAnimationFrame(() => {
      this.updateAllTooltips()
    })
  }

  /**
   * Handle state change events (product selected/removed)
   * @private
   */
  #onStateChanged (event) {
    this.updateAllTooltips()
  }

  /**
   * Handle layer change events
   * Dismisses tooltip for the layer that was navigated to
   * @private
   */
  #onLayerChanged (event) {
    const { subSystemIndex, layerIndex } = event.detail || {}
    if (
      subSystemIndex !== undefined &&
      layerIndex !== undefined &&
      !isNaN(subSystemIndex) &&
      !isNaN(layerIndex)
    ) {
      this.state.dismissTooltip(subSystemIndex, layerIndex)
    }
    this.drawerMessageDismissed = false
    this.updateAllTooltips()
  }

  /**
   * Handle subsystem change events
   * @private
   */
  #onSubsystemChanged (event) {
    this.drawerMessageDismissed = false
    this.updateAllTooltips()
  }

  /**
   * Handle product selected events
   * Stores product info for drawer message display
   * @private
   */
  #onProductSelected (event) {
    const { subSystemIndex, layerIndex, groupIndex } = event.detail || {}

    if (
      subSystemIndex === undefined ||
      layerIndex === undefined ||
      groupIndex === undefined
    ) {
      return
    }

    const productType = this.getProductType(
      subSystemIndex,
      layerIndex,
      groupIndex,
    )
    const nextProductType = this.getNextProductType(
      subSystemIndex,
      layerIndex,
      groupIndex,
    )

    if (productType) {
      this.lastProductAdded = {
        subSystemIndex,
        layerIndex,
        productType,
        nextProductType,
      }
      this.drawerMessageDismissed = false
      this.updateDrawerMessage()
    }
  }

  /**
   * Handle tab click events
   * Dismisses tooltip for the clicked layer
   */
  handleTabClick (event) {
    const clickedTabEl = event.target.closest(this.sharedEls?.tab?.selector)
    if (!clickedTabEl) return

    const subSystemEl = clickedTabEl.closest('[data-system-subsystem-index]')
    const subSystemIndex = parseInt(subSystemEl?.dataset.systemSubsystemIndex)
    if (isNaN(subSystemIndex)) return

    const tabItem = clickedTabEl.closest(
      '.sys-SystemBuilder_TabItem, .sys-SystemBuilder_MobileTabItem',
    )
    const tabsList = tabItem?.parentElement
    const layerIndex = tabsList
      ? Array.from(tabsList.children).indexOf(tabItem)
      : -1

    if (layerIndex === -1) return

    this.state.dismissTooltip(subSystemIndex, layerIndex)
    this.updateAllTooltips()
  }

  /**
   * Handle drawer message dismiss button click
   */
  handleDrawerMessageDismiss (event) {
    event.preventDefault()
    event.stopPropagation()

    this.drawerMessageDismissed = true
    this.lastProductAdded = null
    this.updateDrawerMessage()
  }

  /**
   * Update all tooltips (tab prompts, drawer message)
   */
  updateAllTooltips () {
    this.updateTabPromptTooltips()
    this.updateDrawerMessage()
  }

  /**
   * Update tab prompt tooltips (both desktop and mobile)
   * Shows tooltip above next incomplete layer tab
   * Skips first layer (index 0)
   * Respects dismissed state
   */
  updateTabPromptTooltips () {
    const subSystem = this.state.getSubSystem(this.state.subSystemIndex)
    if (!subSystem) return

    const tabTooltips = this.sharedEls['tab-prompt-tooltip']?.elements || []

    tabTooltips.forEach((tooltip) => {
      tooltip.setAttribute('aria-hidden', 'true')
    })

    const nextIncompleteLayerIndex = this.getNextIncompleteLayer(
      this.state.subSystemIndex,
    )

    if (nextIncompleteLayerIndex === null || nextIncompleteLayerIndex === 0) {
      return
    }

    if (
      this.state.isTooltipDismissed(
        this.state.subSystemIndex,
        nextIncompleteLayerIndex,
      )
    ) {
      return
    }

    const nextLayer = subSystem.layers[nextIncompleteLayerIndex]
    const message = this.getTooltipMessage('tabPrompt', {
      next_layer: nextLayer.title,
    })

    tabTooltips.forEach((tooltip) => {
      const tooltipLayerIndex = parseInt(tooltip.dataset.layerIndex)
      const tooltipSubsystemIndex = parseInt(tooltip.dataset.subsystemIndex)

      if (
        tooltipLayerIndex === nextIncompleteLayerIndex &&
        tooltipSubsystemIndex === this.state.subSystemIndex
      ) {
        const textElement = tooltip.querySelector(
          '.sys-SystemBuilderTab_PromptTooltipText',
        )
        if (textElement) {
          textElement.textContent = message
        }
        tooltip.setAttribute('aria-hidden', 'false')
      }
    })
  }

  /**
   * Get product type for a selected product
   * @param {number} subSystemIndex
   * @param {number} layerIndex
   * @param {number} groupIndex
   * @returns {string|null} Product type or null
   */
  getProductType (subSystemIndex, layerIndex, groupIndex) {
    const products = window.BAO.SystemBuilder.getAllProductsForLayer(
      subSystemIndex,
      layerIndex,
    )

    const product = products.find(
      (p) =>
        p.dataset.systemGroupIndex === String(groupIndex) &&
        p.classList.contains('prd-Card-selected'),
    )

    return product?.dataset.productType || null
  }

  /**
   * Get next product type in the same layer
   * @param {number} subSystemIndex
   * @param {number} layerIndex
   * @param {number} currentGroupIndex
   * @returns {string|null} Next product type or null
   */
  getNextProductType (subSystemIndex, layerIndex, currentGroupIndex) {
    const layer = this.state.getLayer(subSystemIndex, layerIndex)
    if (!layer) {
      return null
    }

    const nextGroupIndex = parseInt(currentGroupIndex) + 1

    if (nextGroupIndex >= layer.groups.length) {
      return null
    }

    const products = window.BAO.SystemBuilder.getAllProductsForLayer(
      subSystemIndex,
      layerIndex,
    )

    const nextProduct = products.find(
      (p) => p.dataset.systemGroupIndex === String(nextGroupIndex),
    )

    return nextProduct?.dataset.productType || null
  }

  /**
   * Update drawer message (shows different messages based on state)
   * - Starter message when no products selected
   * - Product added message on mobile after selection
   */
  updateDrawerMessage () {
    const drawerMessages =
      this.sharedEls['drawer-starter-message']?.elements || []
    const textElements =
      this.sharedEls['drawer-starter-message-text']?.elements || []

    const totalSelectedProducts = this.getTotalSelectedProducts(
      this.state.subSystemIndex,
    )
    const isMobile = window.innerWidth < 894

    drawerMessages.forEach((messageEl) => {
      const textElement = textElements.find(
        (el) =>
          el.closest('[data-system-builder-el="drawer-starter-message"]') ===
          messageEl,
      )

      if (!textElement) return

      let message = ''
      let shouldShow = false

      if (this.drawerMessageDismissed) {
        shouldShow = false
      } else if (totalSelectedProducts === 0) {
        message = this.getTooltipMessage('starterMessage')
        shouldShow = true
      } else if (
        isMobile &&
        this.lastProductAdded &&
        this.lastProductAdded.subSystemIndex === this.state.subSystemIndex
      ) {
        message = this.getDrawerProductMessage()
        shouldShow = !!message
      }

      if (shouldShow && message) {
        textElement.textContent = message
        messageEl.setAttribute('aria-hidden', 'false')
      } else {
        messageEl.setAttribute('aria-hidden', 'true')
      }
    })
  }

  /**
   * Get drawer product message based on lastProductAdded
   * @returns {string} Message text or empty string
   */
  getDrawerProductMessage () {
    if (!this.lastProductAdded || !this.lastProductAdded.productType) {
      return ''
    }

    const currentLayer = this.state.getLayer(
      this.state.subSystemIndex,
      this.state.layerIndex,
    )

    if (this.lastProductAdded.nextProductType) {
      return this.getTooltipMessage('mobile.productAdded', {
        product_type: this.lastProductAdded.productType,
        next_product_type: this.lastProductAdded.nextProductType,
      })
    }

    const isCurrentLayerComplete = this.isLayerComplete(
      this.state.subSystemIndex,
      this.state.layerIndex,
    )

    if (isCurrentLayerComplete && currentLayer) {
      const nextIncompleteLayerIndex = this.getNextIncompleteLayer(
        this.state.subSystemIndex,
      )

      if (nextIncompleteLayerIndex !== null) {
        const nextLayer = this.state.getLayer(
          this.state.subSystemIndex,
          nextIncompleteLayerIndex,
        )
        return this.getTooltipMessage('mobile.layerComplete', {
          layer: currentLayer.title,
          next_layer: nextLayer.title,
        })
      }

      return this.getTooltipMessage('mobile.productAddedLastInLayer', {
        product_type: this.lastProductAdded.productType,
        layer: currentLayer.title,
      })
    }

    return ''
  }

  /**
   * Get tooltip message with variable replacements
   * @param {string} messageKey - Key from window.theme.strings.systemBuilderTooltips
   * @param {Object} replacements - Object with variable names and values
   * @returns {string} Message with variables replaced
   */
  getTooltipMessage (messageKey, replacements = {}) {
    const keys = messageKey.split('.')
    let message = window.theme?.strings?.systemBuilderTooltips

    if (!message) return ''

    for (const key of keys) {
      message = message?.[key]
    }

    if (!message) return ''

    Object.keys(replacements).forEach((key) => {
      const placeholder = `{{ ${key} }}`
      message = message.replace(
        new RegExp(placeholder, 'g'),
        replacements[key],
      )
    })

    return this.decodeHTMLEntities(message)
  }

  decodeHTMLEntities (text) {
    const doc = new DOMParser().parseFromString(text, 'text/html')
    return doc.documentElement.textContent
  }

  /**
   * Check if a layer is complete (all groups have products selected)
   * @param {number} subSystemIndex
   * @param {number} layerIndex
   * @returns {boolean}
   */
  isLayerComplete (subSystemIndex, layerIndex) {
    const layer = this.state.getLayer(subSystemIndex, layerIndex)
    if (!layer) return false

    return layer.groups.every((group) => group.productSelected !== null)
  }

  /**
   * Get next incomplete layer index for a subsystem
   * @param {number} subSystemIndex
   * @returns {number|null} Layer index or null if all complete
   */
  getNextIncompleteLayer (subSystemIndex) {
    const subSystem = this.state.getSubSystem(subSystemIndex)
    if (!subSystem) return null

    for (let i = 0; i < subSystem.layers.length; i++) {
      if (!this.isLayerComplete(subSystemIndex, i)) {
        return i
      }
    }

    return null
  }

  /**
   * Get completion percentage for a layer
   * @param {number} subSystemIndex
   * @param {number} layerIndex
   * @returns {number} Percentage (0-100)
   */
  getCompletionPercent (subSystemIndex, layerIndex) {
    const layer = this.state.getLayer(subSystemIndex, layerIndex)
    if (!layer || layer.groups.length === 0) return 0

    const selectedCount = layer.groups.filter(
      (group) => group.productSelected !== null,
    ).length

    return Math.round((selectedCount / layer.groups.length) * 100)
  }

  /**
   * Get total selected products across all layers in a subsystem
   * @param {number} subSystemIndex
   * @returns {number} Total count
   */
  getTotalSelectedProducts (subSystemIndex) {
    const subSystem = this.state.getSubSystem(subSystemIndex)
    if (!subSystem) {
      return 0
    }

    let total = 0
    subSystem.layers.forEach((layer, layerIdx) => {
      layer.groups.forEach((group, groupIdx) => {
        if (group.productSelected !== null) {
          total++
        }
      })
    })

    return total
  }
}

if (!customElements.get('system-builder-tooltips')) {
  customElements.define('system-builder-tooltips', SystemBuilderTooltips)
}
